import { useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconButton, SvgIcon, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Download01 } from '../../../assets';
import { ItemDocument, OrderDocument } from '../../../app/services/api/orderManagementClient';
import { formatDate } from '../ProductDetails/utils/ProductDocumentUtils';
import { UserContext } from '../../../components/shared/useUser';
import EdnTemplate from './EdnTemplate/EdnTemplate';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import { RetailerEdnOrderModel, client } from '../../../app/services/api/orderManagementClient';

type Props = {
  products: { itemId: number; name: string, sku: string }[];
  productDocuments: (ItemDocument & { productId: number, name: string, sku: string })[];
  orderDocuments?: OrderDocument[];
  orderStatus?: string;
  download: (documentId: number) => void;
  loadData: () => void;
};

const OrderDocuments: React.FC<Props> = ({ products, productDocuments, orderDocuments, orderStatus, download, loadData }) => {
  const { t } = useTranslation('productDetails');
  const theme = useTheme();
  const { hasPermissionTo, selectedStore } = useContext(UserContext);
  const [edn, setEdn] = useState<RetailerEdnOrderModel | undefined>(undefined);
  const [successfulRegeneratedMessage, setSuccessfulRegeneratedMessage] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<boolean>(false);
  const { id } = useParams<{ id: string }>();

  const docsByProduct = products.map(product => ({
    product,
    documents: productDocuments.filter(doc => doc.productId === product.itemId),
  }));

  const downloadBase64File = (base64Data: string, fileName: string) => {
    try {
      const linkSource = `data:application/octet-stream;base64,${base64Data}`;
      const downloadLink = document.createElement('a');
      downloadLink.href = linkSource;
      downloadLink.download = fileName;
      downloadLink.click();
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const fetchEdn = (generateNew: boolean) => {
    if (!id) {
      console.error('No orderId found in URL params');
      setErrorMessage(true);
      return;
    }

    client.getOrderEdn(selectedStore?.storeNumber, generateNew, id)
      .then((result: RetailerEdnOrderModel) => {
        if (result) {
          if (generateNew){
            loadData();
            setSuccessfulRegeneratedMessage(true);
          }
          else {
            setEdn(result);
          }
        }
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .catch((error: any) => {
        console.log(error);
        setErrorMessage(true);
      });
  };

  const formatDotNetDate = (dateString: string): string => {
    if (!dateString) return 'N/A';
    
    // eslint-disable-next-line no-useless-escape
    const match = dateString.match(/\/Date\((\d+)([\+\-]\d{4})?\)\//);
    if (match) {
      const timestamp = parseInt(match[1]);
      const date = new Date(timestamp);
      
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    }
    
    return dateString;
  };

  return (
    <>
      {/* Order Documents Section */}
      {(orderStatus === 'Published' || orderStatus === 'Order Shipped') &&
        orderDocuments &&
        orderDocuments.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <TableContainer sx={{ borderRadius: '12px' }}
            component="div">
            <Table size="small">
              <TableHead sx={{ backgroundColor: theme.palette.custom.gray[200] }}>
                <TableRow>
                  <TableCell colSpan={6}
                    sx={{ fontWeight: 'bold' }}>
                    <Typography>{t('documentsTab.orderDocuments')}</Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ width: '25%' }}>{t('documentsTab.documentName')}</TableCell>
                  <TableCell sx={{ width: '15%' }}>{t('documentsTab.type')}</TableCell>
                  <TableCell sx={{ width: '15%' }}>{t('documentsTab.createdOn')}</TableCell>
                  <TableCell sx={{ width: '15%' }}>{t('documentsTab.LastUpdated')}</TableCell>
                  <TableCell></TableCell>
                  <TableCell sx={{ width: '10%' }}>{t('documentsTab.download')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(orderStatus !== 'Order Shipped'
                  ? orderDocuments.filter((doc) => doc.documentName !== 'Electronic Delivery Note (EDN)')
                  : orderDocuments
                ).map((doc, index) => (
                  <TableRow key={`order-doc-${index}`}>
                    <TableCell sx={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <Typography variant="textXS">{doc.documentName || doc.fileName}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="textXS">{doc.documentType || doc.fileType}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="textXS">{formatDotNetDate(doc.createdOn)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="textXS">{formatDotNetDate(doc.lastUpdated)}</Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>
                      {orderStatus === 'Order Shipped' && doc.documentName === 'Electronic Delivery Note (EDN)' && (
                        <Tooltip title={t('documentsTab.fetchLatest')}
                          arrow>
                          <IconButton onClick={() => fetchEdn(true)}>
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <IconButton onClick={() => {
                          if (orderStatus === 'Order Shipped' && doc.documentName === 'Electronic Delivery Note (EDN)') {
                            fetchEdn(false);
                          } else {
                            downloadBase64File(doc.fileDataBase64, doc.fileName);
                          }
                        }}>
                          <SvgIcon fontSize="small">
                            <Download01 />
                          </SvgIcon>
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      )}


      {/* Product Documents Section */}
      {docsByProduct.map(({ product, documents }) => (
        <div key={product.itemId} 
          style={{ marginBottom: '2rem' }}>
          <TableContainer sx={{ borderRadius: '12px' }} 
            component="div">
            <Table size="small">
              <TableHead sx={{ backgroundColor: theme.palette.custom.gray[200] }}>
                <TableRow>
                  <TableCell colSpan={6} 
                    sx={{ fontWeight: 'bold' }}>
                    <Typography >{product.name} - {product.sku}</Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ width: '25%' }}>{t('documentsTab.documentName')}</TableCell>
                  <TableCell sx={{ width: '15%' }}>{t('documentsTab.effectiveStartDate')}</TableCell>
                  <TableCell sx={{ width: '15%' }}>{t('documentsTab.effectiveEndDate')}</TableCell>
                  <TableCell sx={{ width: '15%' }}>{t('documentsTab.LastUpdated')}</TableCell>
                  <TableCell sx={{ width: '20%' }}>{t('documentsTab.type')}</TableCell>
                  <TableCell sx={{ width: '10%' }}>{t('documentsTab.download')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} 
                      align="center">
                      {t('documentsTab.noDocuments')}
                    </TableCell>
                  </TableRow>
                )}
                {documents.map((row, index) => (
                  row.DocumentId ? (
                    <TableRow key={`${index}${row.Id}`}>
                      <TableCell sx={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <Typography variant="textXS">{row.DocumentName}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant='textXS'>
                          {formatDate(row.EffectiveStartDate, true)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant='textXS'>
                          {formatDate(row.EffectiveEndDate, true)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="textXS">{formatDate(row.LastUpdated)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="textXS">{row.DocumentType}</Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          disabled={!hasPermissionTo(['ProductDetailDocumentsDownload'])}
                          onClick={(e) => {
                            e.stopPropagation();
                            download(row.DocumentId!);
                          }}
                        >
                          <SvgIcon fontSize="small">
                            <Download01 />
                          </SvgIcon>
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ) : null
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      ))}

      <div style={{ position:'absolute',
        top: '0',
        left: '-100%' }}>
        <EdnTemplate
          edn={edn}
          clearEdnContents={() => setEdn(undefined)}
        />
      </div>
      <MessagesSnackbar open={successfulRegeneratedMessage}
        onClose={() => setSuccessfulRegeneratedMessage(false)}
        message={t('documentsTab.ednRegenerated')}
        severity="success"
        duration={3000} />
      <MessagesSnackbar open={errorMessage}
        onClose={() => setErrorMessage(false)}
        message={t('documentsTab.errorOccured')}
        severity="error"
        duration={3000} />
    </>
  );
};

export default OrderDocuments;